import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Sparkles, Zap, Crown, ArrowRight, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";
import { tierLabel } from "@/lib/tiers";
import { useAuth } from "@/context/AuthContext";

// Backend still uses `enterprise` as the internal Stripe tier ID (see
// backend/billing.py). Keep that one mapping localised here ,  every user-facing
// string in this file reads "Business". BLOCK 56.
const BACKEND_BUSINESS_TIER_ID = "enterprise";

const TIER_ICONS = { free: Sparkles, pro: Zap, [BACKEND_BUSINESS_TIER_ID]: Crown, agency: Crown, business: Crown };

export default function Billing() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [tiers, setTiers] = useState([]);
  const [discountMonths, setDiscountMonths] = useState(2);
  const [busy, setBusy] = useState(null);
  const [sub, setSub] = useState(null);
  const [period, setPeriod] = useState("monthly"); // monthly | annual
  const autoTriggeredRef = useRef(false);
  // Iter 59 Task 5 ,  cancel warning modal
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);

  const loadSub = () => api.get("/billing/me/subscription").then((r) => setSub(r.data));

  useEffect(() => {
    api.get("/billing/tiers").then((r) => {
      setTiers(r.data.tiers);
      setDiscountMonths(r.data.annual_discount_months || 2);
    });
    loadSub();
  }, []);

  // Iter 51: auto-launch Stripe checkout when register → billing?plan=pro|business
  useEffect(() => {
    const plan = (params.get("plan") || "").toLowerCase();
    const target = plan === "business" ? BACKEND_BUSINESS_TIER_ID : plan; // map to internal billing ID
    if (!target || autoTriggeredRef.current) return;
    if (!tiers.length) return;
    if (!["pro", BACKEND_BUSINESS_TIER_ID].includes(target)) return;
    if (sub && sub.tier === target && sub.status === "active") return;
    autoTriggeredRef.current = true;
    toast.info(`Continuing to ${plan.toUpperCase()} checkout…`);
    upgrade(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiers, sub, params]);

  const upgrade = async (tierId) => {
    if (tierId === "free") return;
    setBusy(tierId);
    try {
      const r = await api.post("/billing/checkout", {
        tier: tierId,
        billing_period: period,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  const cancelSub = () => setCancelModalOpen(true);

  const confirmCancelSub = async () => {
    setCancelBusy(true);
    try {
      await api.post("/billing/me/cancel");
      toast.success("Auto-renew disabled");
      setCancelModalOpen(false);
      loadSub();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setCancelBusy(false);
    }
  };

  const toggleAutoRenew = async (val) => {
    try {
      await api.patch("/billing/me/auto-renew", { auto_renew: val });
      toast.success(val ? "Auto-renew enabled" : "Auto-renew disabled");
      loadSub();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Billing</div>
        <h1 className="font-heading text-4xl tracking-tight">Subscription Plans</h1>
        <p className="text-sm text-slate-400 mt-1">
          Current plan: <span className="font-mono text-emerald-400 uppercase" data-testid="current-tier">{tierLabel(user?.tier)}</span>
        </p>
      </header>

      {/* Subscription card */}
      {sub && sub.tier !== "free" && (
        <div className="ae-card p-6 border-emerald-500/30">
          <div className="grid md:grid-cols-3 gap-4 items-center">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-1">Active subscription</div>
              <div className="font-heading text-2xl tracking-tight uppercase font-mono" data-testid="sub-tier">{tierLabel(sub.tier)}</div>
              <div className="text-xs text-slate-500 mt-1 capitalize">Status: {sub.subscription_status || "active"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Renews</div>
              <div className="font-mono tabular-nums text-lg mt-1" data-testid="sub-expires">
                {sub.tier_expires_at ? fmtDateTime(sub.tier_expires_at) : "N/A"}
              </div>
              {sub.days_remaining != null && (
                <div className="text-xs text-slate-500 mt-1">{sub.days_remaining} day(s) remaining</div>
              )}
            </div>
            <div className="space-y-2">
              <label className="flex items-center justify-between text-sm cursor-pointer">
                <span>Auto-renew</span>
                <input
                  data-testid="auto-renew-toggle"
                  type="checkbox"
                  checked={sub.auto_renew}
                  onChange={(e) => toggleAutoRenew(e.target.checked)}
                  className="accent-emerald-500 h-4 w-4"
                />
              </label>
              <button onClick={cancelSub} data-testid="cancel-sub-btn" className="text-xs text-rose-400 hover:text-rose-300 inline-flex items-center gap-1">
                <XCircle className="h-3 w-3" /> Cancel subscription
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Period toggle */}
      <div className="flex items-center justify-center gap-2">
        <div className="inline-flex p-1 bg-[#0A0D12] border border-white/10 rounded-sm" data-testid="period-toggle">
          <button
            data-testid="period-monthly"
            onClick={() => setPeriod("monthly")}
            className={`text-xs uppercase tracking-widest px-4 py-1.5 rounded-sm transition-all ${
              period === "monthly" ? "bg-emerald-500 text-black font-semibold" : "text-slate-400 hover:text-slate-100"
            }`}
          >
            Monthly
          </button>
          <button
            data-testid="period-annual"
            onClick={() => setPeriod("annual")}
            className={`text-xs uppercase tracking-widest px-4 py-1.5 rounded-sm transition-all inline-flex items-center gap-2 ${
              period === "annual" ? "bg-emerald-500 text-black font-semibold" : "text-slate-400 hover:text-slate-100"
            }`}
          >
            Annual
            <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm ${period === "annual" ? "bg-black/20 text-black" : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"}`}>
              {discountMonths} months free
            </span>
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {tiers.map((t) => {
          const I = TIER_ICONS[t.id] || Sparkles;
          const isCurrent = user?.tier === t.id;
          const popular = t.popular;
          const price = period === "annual" ? t.annual_price : t.monthly_price;
          const priceSuffix = t.monthly_price === 0 ? "" : period === "annual" ? "/yr" : "/mo";
          return (
            <div
              key={t.id}
              data-testid={`tier-card-${t.id}`}
              className={`ae-card p-6 relative flex flex-col ${popular ? "border-emerald-500/50 shadow-[0_0_60px_-12px_rgba(16,185,129,0.25)]" : ""}`}
            >
              {popular && (
                <div className="absolute -top-2 left-6 text-[10px] uppercase tracking-widest bg-emerald-500 text-black px-2 py-0.5 rounded-sm font-semibold">
                  Most Popular
                </div>
              )}
              <I className="h-7 w-7 text-emerald-400 mb-3" strokeWidth={1.5} />
              <div className="font-heading text-2xl tracking-tight">{t.name}</div>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="font-mono text-4xl font-bold tabular-nums">${price.toLocaleString()}</span>
                {priceSuffix && <span className="text-sm text-slate-500">{priceSuffix}</span>}
              </div>
              {period === "annual" && t.monthly_price > 0 && (
                <div className="text-[10px] uppercase tracking-widest text-emerald-400 mt-1" data-testid={`save-${t.id}`}>
                  Save ${(t.monthly_price * 12 - t.annual_price).toLocaleString()}
                </div>
              )}
              <ul className="mt-5 space-y-2 text-sm flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                    <span className="text-slate-300">{f}</span>
                  </li>
                ))}
              </ul>
              <button
                disabled={isCurrent || busy === t.id}
                onClick={() => upgrade(t.id)}
                data-testid={`tier-cta-${t.id}`}
                className={`mt-6 w-full text-sm py-2.5 rounded-sm transition-all font-semibold inline-flex items-center justify-center gap-2 ${
                  isCurrent
                    ? "bg-white/5 text-slate-500 border border-white/10 cursor-default"
                    : popular
                    ? "bg-emerald-500 text-black hover:bg-emerald-400"
                    : "border border-white/10 text-slate-100 hover:bg-white/5"
                }`}
              >
                {isCurrent ? "Current plan" : t.id === "free" ? "Free forever" : busy === t.id ? "Redirecting…" : <>Upgrade <ArrowRight className="h-4 w-4" /></>}
              </button>
            </div>
          );
        })}
      </div>

      <div className="ae-card p-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">About Billing</div>
        <p className="text-sm text-slate-400 leading-relaxed">
          Payments are processed by Stripe. Subscriptions renew on your selected cadence; you can cancel anytime,
          your tier stays active until the end of the current period. <strong className="text-slate-200">Annual plans save 2 months</strong> vs paying monthly.
        </p>
      </div>

      {/* Iter 59 Task 5 ,  cancel warning modal */}
      {cancelModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="cancel-warning-modal">
          <div className="ae-card p-6 max-w-lg w-full">
            <div className="flex items-start gap-3 mb-4">
              <XCircle className="h-6 w-6 text-rose-400 mt-0.5 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-rose-400 mb-1">Cancel auto-renew</div>
                <div className="font-heading text-xl text-slate-100">Before you cancel, please read this</div>
              </div>
            </div>
            <div className="text-xs text-slate-300 space-y-2 leading-relaxed border border-white/10 rounded-sm p-3 bg-slate-800/40 mb-4">
              <p>Your paid tier stays active until <b>{sub?.tier_expires_at ? fmtDateTime(sub.tier_expires_at) : "period end"}</b>. Access does NOT change immediately.</p>
              <p>When your period ends and you drop to Free, these paid features stop:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-400">
                <li><b>In-flight outreach sequences</b> ,  pending scheduled messages will not send.</li>
                <li><b>Document generation & DocuSign envelopes</b> ,  the Document Library remains readable but new renders are blocked.</li>
                <li><b>Wind-Down Digest</b> ,  the weekly Florida dissolution email stops.</li>
                <li><b>AI Acquisition Assistant</b> ,  chat + doc drafts are Pro/Business only.</li>
              </ul>
              <p className="text-slate-500">You can resubscribe anytime ,  your history and opportunities remain intact.</p>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setCancelModalOpen(false)}
                data-testid="cancel-modal-keep-btn"
                className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border border-white/10 text-slate-300 hover:bg-white/5"
              >
                Keep my subscription
              </button>
              <button
                onClick={confirmCancelSub}
                disabled={cancelBusy}
                data-testid="cancel-modal-confirm-btn"
                className="px-4 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-rose-500/15 text-rose-200 border-rose-500/50 hover:bg-rose-500/25 disabled:opacity-50"
              >
                {cancelBusy ? "Cancelling…" : "Yes, cancel auto-renew"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
